import React, { useState, useEffect } from 'react';
import { Github, Linkedin, Mail, Phone, MapPin, ExternalLink, Code, Cpu, Cloud, Shield, Globe, ChevronDown, Terminal, Zap, Wifi, Radio } from 'lucide-react';

export default function Portfolio() {
  const [activeSection, setActiveSection] = useState('home');
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [particles, setParticles] = useState([]);
  const [hoveredProject, setHoveredProject] = useState(null);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    
    const newParticles = Array.from({ length: 30 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 3 + 1,
      duration: Math.random() * 20 + 10
    }));
    setParticles(newParticles);

    // Detect active section on scroll
    const handleScroll = () => {
      const sections = ['home', 'about', 'skills', 'projects', 'contact'];
      const scrollPosition = window.scrollY + 200;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const skills = {
    languages: ['C', 'C++', 'Python', 'JavaScript', 'Java', 'Flutter', 'VHDL', 'SQL'],
    embedded: ['ESP32', 'STM32', 'Renesas', 'Arduino', 'Raspberry Pi', 'ARM'],
    rtos: ['FreeRTOS', 'Zephyr OS'],
    protocols: ['BLE', 'MQTT', 'LoRa', 'Zigbee', 'WiFi', 'USB-CDC', 'CAN', 'UART', 'I2C', 'SPI'],
    cloud: ['AWS IoT Core', 'AWS Greengrass'],
    tools: ['Git', 'SVN', 'CMake', 'Grafana', 'Eclipse']
  };

  const projects = [
    {
      title: "Portage USB-CDC & Bluetooth",
      company: "Brandt",
      period: "Avril - Septembre 2025 • Orléans ",
      description: "Modernisation technique d'un système embarqué avec développement d'un driver USB-CDC pour émulation port série virtuel et intégration Bluetooth BLE.",
      tech: ['C', 'Renesas RX62N', 'ESP32', 'USB-CDC', 'Bluetooth BLE'],
      icon: Radio,
      gradient: "from-purple-500 to-pink-500",
      emoji: "🔥"
    },
    {
      title: "PowerSpyCli",
      company: "Projet Open Source",
      period: "2025",
      description: "Outil de mesure de consommation énergétique pour l'industrie avec interface cross-platform et communication Bluetooth RFCOMM.",
      tech: ['Python', 'Bluetooth RFCOMM', 'Cross-Platform'],
      icon: Zap,
      gradient: "from-yellow-500 to-orange-500",
      emoji: "⚡"
    },
    {
      title: "Librairie Machine à États",
      company: "Projet Académique",
      period: "2024",
      description: "Portage d'une librairie de machine à états pour environnement temps réel avec contraintes mémoire strictes sur FreeRTOS.",
      tech: ['C++', 'FreeRTOS', 'Raspberry Pi', 'Git'],
      icon: Terminal,
      gradient: "from-green-500 to-emerald-500",
      emoji: "💻"
    },
    {
      title: "Colliers Connectés IoT",
      company: "Projet IoT",
      period: "2025",
      description: "Système IoT complet pour monitoring de données capteurs avec communication longue portée LoRa et intégration cloud MQTT.",
      tech: ['MicroPython', 'ESP32', 'LoRa', 'MQTT', 'Cloud'],
      icon: Wifi,
      gradient: "from-blue-500 to-cyan-500",
      emoji: "🐴"
    }
  ];

  const scrollToSection = (section) => {
    setActiveSection(section);
    document.getElementById(section)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden relative">
      {/* Animated Background */}
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-950 via-purple-950 to-black opacity-50" />
        {particles.map((particle) => (
          <div
            key={particle.id}
            className="absolute rounded-full bg-blue-400 opacity-20"
            style={{
              left: `${particle.x}%`,
              top: `${particle.y}%`,
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              animation: `pulse ${particle.duration}s infinite`
            }}
          />
        ))}
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'linear-gradient(rgba(56,189,248,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(56,189,248,0.3) 1px, transparent 1px)',
          backgroundSize: '100px 100px'
        }} />
      </div>

      {/* Glowing cursor effect */}
      <div
        className="fixed w-96 h-96 rounded-full pointer-events-none z-10 transition-opacity duration-300"
        style={{
          left: mousePosition.x - 192,
          top: mousePosition.y - 192,
          background: 'radial-gradient(circle, rgba(59,130,246,0.15) 0%, transparent 70%)',
        }}
      />

      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-black/40 backdrop-blur-xl z-50 border-b border-blue-500/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="text-2xl font-bold">
              <span className="text-white">&lt;</span>
              <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                AB
              </span>
              <span className="text-white">/&gt;</span>
            </div>
            <div className="hidden md:flex space-x-8">
              {['home', 'about', 'skills', 'projects', 'contact'].map((section) => (
                <button
                  key={section}
                  onClick={() => scrollToSection(section)}
                  className={`relative capitalize transition-colors group ${
                    activeSection === section ? 'text-blue-400' : 'text-gray-400'
                  }`}
                >
                  <span className="hover:text-blue-400 transition-colors">
                    {section === 'home' ? 'Accueil' : section === 'about' ? 'À propos' : section === 'skills' ? 'Compétences' : section === 'projects' ? 'Projets' : 'Contact'}
                  </span>
                  <span className={`absolute -bottom-2 left-0 h-0.5 bg-gradient-to-r from-blue-400 to-purple-400 transition-all duration-300 ${
                    activeSection === section ? 'w-full' : 'w-0 group-hover:w-full'
                  }`} />
                </button>
              ))}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="min-h-screen flex items-center justify-center pt-20 px-4 relative z-20">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div className="space-y-8">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/30 rounded-full backdrop-blur-sm">
                <div className="w-2 h-2 rounded-full bg-green-400 animate-ping" />
                <span className="text-sm text-blue-300">Disponible pour nouvelles opportunités</span>
              </div>
              <h1 className="text-6xl md:text-7xl font-bold leading-tight">
                <span className="block text-gray-400">Bonjour, je suis</span>
                <span className="block bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                  Ali Bouhjira
                </span>
              </h1>
              <div className="flex items-center gap-2 text-xl text-gray-300">
                <Terminal className="text-blue-400 animate-pulse" />
                <span className="font-mono">Ingénieur Logiciel Embarqué & IoT</span>
              </div>
            </div>
            <p className="text-xl text-gray-400 leading-relaxed">
              Je conçois des <span className="text-blue-400 font-semibold">solutions firmware</span>, 
              en alliant performance, sécurité et expérience utilisateur.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="https://github.com/alibouhjira" target="_blank" rel="noopener noreferrer" 
                 className="group flex items-center gap-2 px-6 py-4 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 rounded-xl transition-all transform hover:scale-105 hover:shadow-lg hover:shadow-blue-500/50">
                <Github size={20} />
                <span className="font-semibold">GitHub</span>
              </a>
              <a href="https://linkedin.com/in/ali-bouhjira-iot" target="_blank" rel="noopener noreferrer"
                 className="group flex items-center gap-2 px-6 py-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl transition-all transform hover:scale-105 backdrop-blur-sm">
                <Linkedin size={20} className="text-blue-400" />
                <span className="font-semibold">LinkedIn</span>
              </a>
             
            </div>
          </div>
          
          <div className="flex justify-center relative">
            <div className="relative w-80 h-80">
              <div className="absolute inset-0 rounded-full border-2 border-blue-500/30 animate-spin" style={{ animationDuration: '20s' }} />
              <div className="absolute inset-4 rounded-full border-2 border-purple-500/30" style={{ animation: 'spin 15s linear infinite reverse' }} />
              <div className="relative w-full h-full rounded-full bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 p-1">
                <div className="w-full h-full rounded-full bg-black flex items-center justify-center text-7xl font-bold border-4 border-black">
                  <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">AB</span>
                </div>
              </div>
              <div className="absolute top-0 right-0 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-3 shadow-lg shadow-blue-500/50 animate-bounce" style={{ animationDuration: '3s' }}>
                <Cpu size={28} />
              </div>
              <div className="absolute bottom-10 left-0 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-3 shadow-lg shadow-purple-500/50 animate-bounce" style={{ animationDuration: '3s', animationDelay: '1s' }}>
                <Code size={28} />
              </div>
              <div className="absolute top-20 -right-4 bg-gradient-to-br from-pink-500 to-pink-600 rounded-2xl p-3 shadow-lg shadow-pink-500/50 animate-bounce" style={{ animationDuration: '3s', animationDelay: '0.5s' }}>
                <Wifi size={28} />
              </div>
            </div>
          </div>
        </div>
        <button 
          onClick={() => scrollToSection('about')}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce"
        >
          <ChevronDown size={40} className="text-blue-400" />
        </button>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 px-4 relative z-20">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-5xl font-bold mb-16 text-center">
            <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
              À propos de moi
            </span>
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-2xl p-8 backdrop-blur-sm hover:border-blue-500/50 transition-all transform hover:scale-105 hover:shadow-xl hover:shadow-blue-500/20">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl">
                  <Code size={24} />
                </div>
                <h3 className="text-2xl font-bold">Formation</h3>
              </div>
              <div className="space-y-6">
                <div className="relative pl-6 border-l-2 border-blue-500/50">
                  <div className="absolute left-0 top-0 w-3 h-3 bg-blue-500 rounded-full -translate-x-[7px]" />
                  <div className="font-semibold text-lg text-blue-300">Master Systèmes Embarqués et IoT</div>
                  <div className="text-gray-400 text-sm">Université de Lille • 2023-2025</div>
                  <div className="inline-block mt-2 px-3 py-1 bg-blue-500/20 border border-blue-500/50 rounded-full text-sm">
                    Mention Bien
                  </div>
                </div>
                <div className="relative pl-6 border-l-2 border-purple-500/50">
                  <div className="absolute left-0 top-0 w-3 h-3 bg-purple-500 rounded-full -translate-x-[7px]" />
                  <div className="font-semibold text-lg text-purple-300">Licence Informatique</div>
                  <div className="text-gray-400 text-sm">UPHF • 2020-2023</div>
                </div>
              </div>
            </div>
            
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-2xl p-8 backdrop-blur-sm hover:border-purple-500/50 transition-all transform hover:scale-105 hover:shadow-xl hover:shadow-purple-500/20">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl">
                    <Mail size={24} />
                  </div>
                  <h3 className="text-2xl font-bold">Contact</h3>
                </div>
                <div className="space-y-4">
                  <a href="mailto:ali.bouhjira59950@gmail.com" className="flex items-center gap-3 hover:text-blue-400 transition-colors">
                    <Mail className="text-blue-400" size={20} />
                    <span className="font-mono text-sm">ali.bouhjira59950@gmail.com</span>
                  </a>
                  <div className="flex items-center gap-3">
                    <Phone className="text-purple-400" size={20} />
                    <span className="font-mono text-sm">07 67 48 29 58</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <MapPin className="text-pink-400" size={20} />
                    <span className="text-sm">Douai (59) - Mobile France</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/20 rounded-2xl p-8 backdrop-blur-sm hover:border-green-500/50 transition-all transform hover:scale-105">
                <div className="flex items-center gap-3">
                  <Shield className="text-green-400" size={24} />
                  <div>
                    <h4 className="font-semibold">Certifications</h4>
                    <p className="text-sm text-gray-400">Habilitation électrique BE essai mesure</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section id="skills" className="py-32 px-4 relative z-20">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-5xl font-bold mb-16 text-center">
            <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
              Compétences Techniques
            </span>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: 'Langages', items: skills.languages, icon: Code, color: 'blue' },
              { title: 'Microcontrôleurs', items: skills.embedded, icon: Cpu, color: 'purple' },
              { title: 'Protocoles IoT', items: skills.protocols, icon: Radio, color: 'pink' },
              { title: 'RTOS', items: skills.rtos, icon: Terminal, color: 'green' },
              { title: 'Cloud', items: skills.cloud, icon: Cloud, color: 'cyan' },
              { title: 'Outils', items: skills.tools, icon: Code, color: 'orange' }
            ].map((category, idx) => {
              const Icon = category.icon;
              return (
                <div 
                  key={idx}
                  className="group bg-gradient-to-br from-white/5 to-white/0 border border-white/10 rounded-2xl p-6 backdrop-blur-sm hover:border-white/30 transition-all transform hover:scale-105 hover:shadow-xl"
                >
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl">
                      <Icon size={24} />
                    </div>
                    <h3 className="text-xl font-bold">{category.title}</h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {category.items.map((item) => (
                      <span 
                        key={item} 
                        className="px-3 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/30 rounded-lg text-sm transition-all cursor-default"
                      >
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="py-32 px-4 relative r-100 z-20">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-5xl font-bold mb-16 text-center">
            <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
              Projets Réalisés
            </span>
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {projects.map((project, index) => {
              const Icon = project.icon;
              return (
                <div 
                  key={index}
                  className="group relative bg-gradient-to-br from-white/5 to-white/0 border border-white/10 rounded-3xl p-8 backdrop-blur-sm hover:border-white/30 transition-all transform hover:scale-105 hover:shadow-2xl overflow-hidden"
                  onMouseEnter={() => setHoveredProject(index)}
                  onMouseLeave={() => setHoveredProject(null)}
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${project.gradient} opacity-0 group-hover:opacity-10 transition-opacity rounded-3xl`} />
                  
                  {/* Animated Effects */}
                  {hoveredProject === index && (
                    <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-3xl">
                      {/* Lightning Effect for PowerSpy */}
                      {index === 1 && (
                        <div className="absolute top-12 right-12 z-12">
                          <svg width="160" height="180" viewBox="0 0 160 180" style={{ filter: 'drop-shadow(0 0 15px rgba(250,204,21,0.9))' }}>
                            <path
                              d="M 80 20 L 60 80 L 90 80 L 70 150"
                              stroke="#fbbf24"
                              strokeWidth="6"
                              fill="none"
                              className="animate-lightning"
                              strokeLinecap="butt"
                              strokeLinejoin="miter"
                            />
                            <path
                              d="M 110 15 L 95 70 L 120 70 L 105 130"
                              stroke="#fde047"
                              strokeWidth="5"
                              fill="none"
                              className="animate-lightning"
                              style={{ animationDelay: '0.3s' }}
                              strokeLinecap="butt"
                              strokeLinejoin="miter"
                            />
                          </svg>
                        </div>
                      )}
                      
                     {/* Embedded System Abstract Animation */}
{index === 0 && (
  <div className="absolute top-8 right-5 z-20 my-4 opacity-60 pointer-events-none">
    <svg width="220" height="160" viewBox="0 0 220 160">
      
      {/* Central MCU */}
      <rect x="80" y="50" width="60" height="60" rx="8"
        fill="#020617" stroke="#3b82f6" strokeWidth="2"/>
      <text x="110" y="85"
        textAnchor="middle"
        fontSize="10"
        fill="#93c5fd"
        fontFamily="monospace">
        MCU
      </text>

      {/* IO Pins */}
      {[0,1,2,3].map(i=>(
        <rect key={i}
          x="70" y={55 + i*12}
          width="10" height="6"
          fill="#1e40af"/>
      ))}
      {[0,1,2,3].map(i=>(
        <rect key={i}
          x="140" y={55 + i*12}
          width="10" height="6"
          fill="#1e40af"/>
      ))}

      {/* Communication lines */}
      <line x1="40" y1="80" x2="80" y2="80"
        stroke="#22c55e" strokeWidth="2"
        strokeDasharray="6,6"
        className="animate-data-flow"/>
      <line x1="140" y1="80" x2="190" y2="80"
        stroke="#3b82f6" strokeWidth="2"
        strokeDasharray="6,6"
        className="animate-data-flow"
        style={{ animationDelay: '0.5s' }}/>

      {/* Labels */}
      <text x="28" y="75" fontSize="9" fill="#22c55e" fontFamily="monospace">
        USB
      </text>
      <text x="192" y="75" fontSize="9" fill="#3b82f6" fontFamily="monospace">
        UART
      </text>

      {/* Status LED */}
      
    </svg>
  </div>
)}

                      
                      {/* Retro Computer Effect */}
                      {index === 2 && (
                        <div className="absolute top-20 right-20 z-20 animate-glitch">
                          <svg width="140" height="110" viewBox="0 0 140 110">
                            <rect x="10" y="10" width="120" height="70" fill="#1f2937" stroke="#10b981" strokeWidth="3" rx="3" />
                            <rect x="18" y="18" width="104" height="50" fill="#0f172a" />
                            <text x="70" y="48" fill="#10b981" fontSize="14" fontFamily="monospace" textAnchor="middle" className="animate-blink" fontWeight="bold">
                              &gt;Hire me!_
                            </text>
                            <rect x="45" y="82" width="50" height="4" fill="#1f2937" />
                            <rect x="52" y="86" width="36" height="12" fill="#374151" rx="2" />
                          </svg>
                        </div>
                      )}
                      
                      {/* IoT Sensor with Signal Waves */}
                      {index === 3 && (
                        <div className="absolute top-8 right-8 z-20">
                          <svg width="140" height="140" viewBox="0 0 140 140">
                            {/* Signal waves */}
                            <circle 
                              cx="70" 
                              cy="70" 
                              r="60" 
                              fill="none" 
                              stroke="#3b82f6" 
                              strokeWidth="2" 
                              opacity="0.6"
                              className="animate-signal-wave"
                            />
                            <circle 
                              cx="70" 
                              cy="70" 
                              r="45" 
                              fill="none" 
                              stroke="#60a5fa" 
                              strokeWidth="2.5" 
                              opacity="0.7"
                              className="animate-signal-wave"
                              style={{ animationDelay: '0.5s' }}
                            />
                            <circle 
                              cx="70" 
                              cy="70" 
                              r="30" 
                              fill="none" 
                              stroke="#93c5fd" 
                              strokeWidth="3" 
                              opacity="0.8"
                              className="animate-signal-wave"
                              style={{ animationDelay: '1s' }}
                            />
                            
                            {/* IoT Device - Smart Collar Tag */}
                            <rect x="50" y="50" width="40" height="40" rx="8" fill="#1e40af" stroke="#3b82f6" strokeWidth="2.5" />
                            
                            {/* LED indicator */}
                            <circle cx="70" cy="60" r="4" fill="#22d3ee" className="animate-pulse" />
                            
                            {/* GPS icon */}
                            <path 
                              d="M 70 68 L 65 80 L 70 77 L 75 80 Z" 
                              fill="#10b981" 
                              className="animate-pulse"
                              style={{ animationDelay: '0.3s' }}
                            />
                            
                            {/* Data streams */}
                            <line x1="70" y1="90" x2="70" y2="105" stroke="#60a5fa" strokeWidth="2.5" strokeDasharray="4,4" className="animate-data-flow" />
                            <line x1="70" y1="35" x2="70" y2="50" stroke="#60a5fa" strokeWidth="2.5" strokeDasharray="4,4" className="animate-data-flow" style={{ animationDelay: '0.5s' }} />
                            <line x1="35" y1="70" x2="50" y2="70" stroke="#60a5fa" strokeWidth="2.5" strokeDasharray="4,4" className="animate-data-flow" style={{ animationDelay: '0.25s' }} />
                            <line x1="90" y1="70" x2="105" y2="70" stroke="#60a5fa" strokeWidth="2.5" strokeDasharray="4,4" className="animate-data-flow" style={{ animationDelay: '0.75s' }} />
                            
                            {/* Status text */}
                            <text x="70" y="125" fill="#3b82f6" fontSize="11" fontFamily="monospace" textAnchor="middle" className="animate-pulse" fontWeight="bold">
                              CONNECTED
                            </text>
                          </svg>
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div className="relative z-10">
                    <div className="flex items-start justify-between mb-4">
                      <div className={`p-4 bg-gradient-to-br ${project.gradient} rounded-2xl`}>
                        <Icon size={32} />
                      </div>
                      {project.period && (
                        <span className="px-3 py-1 bg-white/5 border border-white/10 rounded-full text-xs">
                          {project.period}
                        </span>
                      )}
                    </div>
                    
                    <h3 className="text-2xl font-bold mb-2">
                      {project.title}
                    </h3>
                    <p className="text-sm text-blue-400 mb-4">{project.company}</p>
                    <p className="text-gray-400 mb-6 leading-relaxed">{project.description}</p>
                    
                    <div className="flex flex-wrap gap-2">
                      {project.tech.map((tech) => (
                        <span 
                          key={tech} 
                          className="px-3 py-1 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-xs font-mono transition-all"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          
          <div className="mt-16 text-center">
            <a 
              href="https://github.com/alibouhjira" 
              target="_blank" 
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 rounded-2xl transition-all transform hover:scale-110 text-lg font-semibold shadow-lg shadow-blue-500/50 hover:shadow-blue-500/80"
            >
              <Github size={28} />
              Découvrir tous mes projets
              <ExternalLink size={24} />
            </a>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-32 px-4 relative z-20">
        <div className="max-w-4xl mx-auto text-center">
          <div className="mb-8">
            <div className="inline-block p-4 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full mb-6 animate-pulse">
              <Mail size={40} />
            </div>
          </div>
          <h2 className="text-5xl font-bold mb-6">
            <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
              Créons ensemble
            </span>
          </h2>
          <p className="text-2xl text-gray-400 mb-12 leading-relaxed">
            À la recherche d'opportunités en <span className="text-blue-400">développement embarqué</span> et <span className="text-purple-400">IoT</span>.
            <br />Discutons de votre prochain projet !
          </p>
          <div className="flex flex-wrap justify-center gap-6">
            <a 
              href="mailto:ali.bouhjira59950@gmail.com"
              className="group flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 rounded-2xl transition-all transform hover:scale-110 text-lg font-semibold shadow-lg shadow-blue-500/50"
            >
              <Mail size={24} />
              Envoyez-moi un message
            </a>
           
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-20 py-12 px-4 border-t border-white/10 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-4">
            <div className="flex justify-center gap-6 mb-6">
              <a href="https://github.com/alibouhjira" target="_blank" rel="noopener noreferrer" 
                 className="p-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl transition-all transform hover:scale-110">
                <Github size={24} />
              </a>
              <a href="https://linkedin.com/in/ali-bouhjira-iot" target="_blank" rel="noopener noreferrer"
                 className="p-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl transition-all transform hover:scale-110">
                <Linkedin size={24} />
              </a>
              
            </div>
            <p className="text-gray-400">
              © 2026 Ali Bouhjira - Ingénieur Logiciel Embarqué & IoT
            </p>
            <p className="text-sm text-gray-500">
              Conçu avec <span className="text-red-500">❤️</span> et React
            </p>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes lightning {
          0%, 100% { opacity: 0; }
          10%, 20%, 30% { opacity: 1; }
          15%, 25% { opacity: 0.3; }
        }
        .animate-lightning {
          animation: lightning 1.5s ease-in-out;
        }
        
        @keyframes induction-pulse {
          0%, 100% { 
            transform: scale(1);
            opacity: 0.8;
          }
          50% { 
            transform: scale(1.1);
            opacity: 1;
          }
        }
        .animate-induction-pulse {
          animation: induction-pulse 1.5s ease-in-out infinite;
          transform-origin: center;
        }
        
        @keyframes heat-wave {
          0% { 
            opacity: 0;
            transform: translateY(0);
          }
          50% { 
            opacity: 0.6;
          }
          100% { 
            opacity: 0;
            transform: translateY(-20px);
          }
        }
        .animate-heat-wave {
          animation: heat-wave 2s ease-out infinite;
        }
        
        @keyframes glitch {
          0%, 100% { transform: translate(0, 0); filter: hue-rotate(0deg); }
          20% { transform: translate(-2px, 2px); filter: hue-rotate(10deg); }
          40% { transform: translate(2px, -2px); filter: hue-rotate(-10deg); }
          60% { transform: translate(-1px, -1px); filter: hue-rotate(5deg); }
          80% { transform: translate(1px, 1px); filter: hue-rotate(-5deg); }
        }
        .animate-glitch {
          animation: glitch 2s ease-in-out infinite;
        }
        
        @keyframes blink {
          0%, 49%, 100% { opacity: 1; }
          50%, 99% { opacity: 0; }
        }
        .animate-blink {
          animation: blink 1s step-end infinite;
        }
        
        @keyframes signal-wave {
          0% { 
            transform: scale(0.5);
            opacity: 1;
          }
          100% { 
            transform: scale(1.5);
            opacity: 0;
          }
        }
        .animate-signal-wave {
          animation: signal-wave 2s ease-out infinite;
          transform-origin: center;
        }
        
        @keyframes data-flow {
          0%, 100% { 
            stroke-dashoffset: 0;
            opacity: 0.4;
          }
          50% { 
            stroke-dashoffset: 10;
            opacity: 1;
          }
        }
        .animate-data-flow {
          animation: data-flow 1s linear infinite;
        }
      `}</style>
    </div>
  );
}

/* ================= UTILS (Tailwind) ================= */
/*
.btn-primary {
  @apply flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg transition font-semibold;
}
.btn-secondary {
  @apply flex items-center gap-2 px-6 py-3 bg-slate-700 hover:bg-slate-600 rounded-lg transition;
}
.section-title {
  @apply text-4xl font-bold text-center mb-16 bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent;
}
.card {
  @apply bg-slate-800/60 backdrop-blur border border-blue-500/20 rounded-xl p-6 shadow-xl;
}
.card-title {
  @apply text-2xl font-semibold text-blue-400 mb-3;
}
*/


// import React, { useState, useEffect } from "react";
// import { useQuery } from '@tanstack/react-query';
// import {
//   FaEnvelope, FaPhone, FaMapMarkerAlt, FaUser, FaGraduationCap, FaReact,
//   FaSitemap, FaAngleDown, FaAngleUp, FaGithub, FaBolt, FaHorse,
//   FaLaptopCode, FaLinkedin, FaWhatsapp, FaFilePdf
// } from "react-icons/fa";
// import { BsMicrosoftTeams } from "react-icons/bs";



// const CV = () => {
//   const [isOpen, setIsOpen] = useState(false);
//   const [pdfUrl, setPdfUrl] = useState("");
//   const [scrolled, setScrolled] = useState(false);
//   const [activeSection, setActiveSection] = useState('header');
//   const [showScrollTop, setShowScrollTop] = useState(false);

//   const openModal = (url) => {
//     setPdfUrl(url);
//     setIsOpen(true);
//   };

//   const closeModal = () => {
//     setIsOpen(false);
//     setPdfUrl("");
//   };

//   const documents = [
//     { label: "Mémoire de recherche", href: "MemoireMaster_AliBouhjira.pdf" },
//   ];

//   const [showDetails, setShowDetails] = useState({
//     stateMachine: false,
//     powerSpyCli: false,
//     horseCollar: false,
//     webMobileStage: false,
//   });

//   const toggleDetails = (projectId) => {
//     setShowDetails(prevState => ({
//       ...prevState,
//       [projectId]: !prevState[projectId],
//     }));
//   };


//   const scrollToSection = (sectionId) => {
//     const element = document.getElementById(sectionId);
//     if (element) {
//       const offset = 100;
//       const elementPosition = element.getBoundingClientRect().top;
//       const offsetPosition = elementPosition + window.pageYOffset - offset;

//       window.scrollTo({
//         top: offsetPosition,
//         behavior: 'smooth'
//       });
//     }
//   };

//   const navItems = [
//     { id: 'header', icon: FaUser, label: 'Profil' },
//     { id: 'formation', icon: FaGraduationCap, label: 'Formation' },
//     { id: 'stage', icon: FaLaptopCode, label: 'Expérience' },
//     { id: 'competences', icon: FaReact, label: 'Compétences' },
//     { id: 'projets', icon: FaGithub, label: 'Projets' },
//     { id: 'soft-skills', icon: '💡', label: 'Soft Skills' },
//     { id: 'references', icon: '👥', label: 'Références' },
//     { id: 'interets', icon: '⚽', label: 'Passe-temps' },
//     { id: 'memoire', icon: FaFilePdf, label: 'Mémoire' },
//     { id: 'partage', icon: '🔗', label: 'Partager' }
//   ];

//   useEffect(() => {
//     const handleScroll = () => {

//       setScrolled(window.scrollY > 50);
//       setShowScrollTop(window.scrollY > 500);

//       const sections = ['header', 'formation', 'stage', 'competences', 'projets', 'soft-skills', 'references', 'interets', 'memoire', 'partage'];

//       for (let section of sections) {
//         const element = document.getElementById(section);
//         if (element) {
//           const rect = element.getBoundingClientRect();
//           if (rect.top <= 150 && rect.bottom >= 150) {
//             setActiveSection(section);
//             break;
//           }
//         }
//       }
//       const isBottom =
//         window.innerHeight + window.scrollY >=
//         document.documentElement.scrollHeight - 10;

//       if (isBottom) {
//         setActiveSection('partage');
//         return;
//       }
//     };

//     window.addEventListener('scroll', handleScroll);
//     return () => window.removeEventListener('scroll', handleScroll);
//   }, []);


//   const shareOnLinkedIn = () => {
//     window.open(`https://www.linkedin.com/sharing/share-offsite/?url=www.ali-bouhjira.fr`, '_blank');
//   };

//   const shareOnWhatsApp = () => {
//     window.open(`https://wa.me/?text=Ce%20cv%20pourrait%20être%20intéressant:%20www.ali-bouhjira.fr`, '_blank');
//   };

//   const shareByEmail = () => {
//     window.location.href = `mailto:?subject=CV%20Ali%20Bouhjira&body=Ce%20cv%20pourrait%20être%20intéressant:%20www.ali-bouhjira.fr`;
//   };

//   const shareOnTeams = () => {
//     const message = "Salut, ce cv pourrait t'interesser : www.ali-bouhjira.fr";
//     const teamsUrl = `https://teams.microsoft.com/l/chat/0/0?users=&message=${encodeURIComponent(message)}`;
//     window.open(teamsUrl, "_blank");
//   };

// const langages = [
//     { name: "C", level: "expert" },
//     { name: "C++", level: "notions" },
//     { name: "Python", level: "notions" },
//     { name: "Micropython", level: "notions" },
//     { name: "Java", level: "notions" },
//     { name: "JavaScript", level: "notions" },
//     { name: "Flutter", level: "notions" },
//     { name: "Bash", level: "notions" },
//     { name: "HTML", level: "notions" },
//     { name: "CSS", level: "notions" },
//     { name: "VueJS", level: "notions" },
//     { name: "VHDL", level: "notions" },
//     { name: "SQL", level: "notions" },
//     { name: "Assembleur x86_64", level: "notion" }
//   ];
//   // ========================================
//   // SYSTÈME DE TRACKING DISCORD AMÉLIORÉ
//   // À ajouter dans votre composant CV, après les déclarations de state
//   // ========================================


//   useEffect(() => {
//     console.log('🔍 useEffect Discord initialisé');
//     const startTime = Date.now();

//     // TEST IMMÉDIAT - Notification simple
//     const testNotification = () => {
//       console.log('📤 Tentative d\'envoi notification...');

//       const payload = {
//         content: `🧪 TEST - Nouvelle visite à ${new Date().toLocaleTimeString()}`
//       };

//       fetch(
//         "https://discord.com/api/webhooks/1346146878957420585/-K6coLPlwk0EBbuGUfhxzq0zR4FvWBy-QXZ6Fhcxs5HgHHE_HVBEXJcTgDKlyqE0BZPf",
//         {
//           method: "POST",
//           headers: { "Content-Type": "application/json" },
//           body: JSON.stringify(payload)
//         }
//       )
//         .then(response => {
//           console.log('✅ Réponse Discord:', response.status);
//           if (response.ok) {
//             console.log('✅ Notification envoyée avec succès !');
//           } else {
//             console.error('❌ Erreur Discord:', response.status);
//           }
//         })
//         .catch(error => {
//           console.error('❌ Erreur fetch:', error);
//         });
//     };

//     // Envoyer immédiatement pour tester
//     testNotification();

//     // Fonction pour obtenir des informations sur le visiteur
//     const getVisitorInfo = () => {
//       const params = new URLSearchParams(window.location.search);
//       return {
//         timestamp: new Date().toLocaleString('fr-FR', { timeZone: 'Europe/Paris' }),
//         url: window.location.href,
//         referrer: document.referrer || 'Accès direct',
//         userAgent: navigator.userAgent,
//         language: navigator.language,
//         screenResolution: `${window.screen.width}x${window.screen.height}`,
//         viewport: `${window.innerWidth}x${window.innerHeight}`,
//         platform: navigator.platform,
//         recruteur: params.get('recruteur') || 'Non spécifié',
//         source: params.get('utm_source') || params.get('source') || 'Non spécifié'
//       };
//     };

//     // Fonction pour envoyer une notification Discord enrichie
//     const sendDiscordNotification = (type, additionalData = {}) => {
//       console.log(`📤 Envoi notification ${type}...`);
//       const info = getVisitorInfo();
//       const duration = Math.round((Date.now() - startTime) / 1000);

//       let embed = {
//         title: type === 'entry' ? '👋 Nouvelle visite sur le CV' : '👋 Fin de visite',
//         color: type === 'entry' ? 3066993 : 15158332,
//         fields: [
//           {
//             name: '⏱️ Durée de visite',
//             value: `${Math.floor(duration / 60)}min ${duration % 60}s`,
//             inline: true
//           },
//           {
//             name: '🌐 Langue',
//             value: info.language,
//             inline: true
//           },
//           {
//             name: '💻 Plateforme',
//             value: info.platform,
//             inline: true
//           },
//           {
//             name: '🔗 URL',
//             value: info.url,
//             inline: false
//           },
//           {
//             name: '📊 Résolution',
//             value: `Écran: ${info.screenResolution} | Fenêtre: ${info.viewport}`,
//             inline: false
//           },
//           {
//             name: '🔍 Provenance',
//             value: info.referrer,
//             inline: false
//           }
//         ],
//         footer: {
//           text: `CV Ali Bouhjira • ${info.timestamp}`
//         },
//         timestamp: new Date().toISOString()
//       };

//       if (info.recruteur !== 'Non spécifié') {
//         embed.fields.unshift({
//           name: '🎯 Recruteur',
//           value: info.recruteur,
//           inline: true
//         });
//       }

//       if (info.source !== 'Non spécifié') {
//         embed.fields.push({
//           name: '📍 Source',
//           value: info.source,
//           inline: true
//         });
//       }

//       const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
//       embed.fields.push({
//         name: '📱 Type d\'appareil',
//         value: isMobile ? 'Mobile' : 'Desktop',
//         inline: true
//       });

//       if (Object.keys(additionalData).length > 0) {
//         Object.entries(additionalData).forEach(([key, value]) => {
//           embed.fields.push({
//             name: key,
//             value: String(value),
//             inline: true
//           });
//         });
//       }

//       const payload = {
//         embeds: [embed],
//         username: 'CV Analytics',
//         avatar_url: 'https://cdn-icons-png.flaticon.com/512/3135/3135715.png'
//       };

//       // Utiliser fetch avec promesse pour débugger
//       fetch(
//         "https://discord.com/api/webhooks/1346146878957420585/-K6coLPlwk0EBbuGUfhxzq0zR4FvWBy-QXZ6Fhcxs5HgHHE_HVBEXJcTgDKlyqE0BZPf",
//         {
//           method: "POST",
//           headers: { "Content-Type": "application/json" },
//           body: JSON.stringify(payload),
//           keepalive: true
//         }
//       )
//         .then(response => {
//           console.log(`✅ Notification ${type} envoyée:`, response.status);
//         })
//         .catch(error => {
//           console.error(`❌ Erreur notification ${type}:`, error);
//         });
//     };

//     // Notification d'entrée après 3 secondes
//     const entryTimeout = setTimeout(() => {
//       console.log('⏰ Timeout entrée déclenché');
//       sendDiscordNotification('entry');
//     }, 3000);

//     // Notification de sortie
//     const handleUnload = () => {
//       console.log('🚪 Départ détecté');
//       sendDiscordNotification('exit');
//     };

//     // Notification de visibilité
//     let hiddenTime = null;
//     const handleVisibilityChange = () => {
//       if (document.hidden) {
//         console.log('👁️ Page cachée');
//         hiddenTime = Date.now();
//       } else if (hiddenTime) {
//         const timeAway = Math.round((Date.now() - hiddenTime) / 1000);
//         console.log(`👁️ Page visible après ${timeAway}s`);
//         if (timeAway > 30) {
//           sendDiscordNotification('entry', {
//             '🔄 Retour': `Après ${timeAway}s d'absence`
//           });
//         }
//         hiddenTime = null;
//       }
//     };

//     window.addEventListener("beforeunload", handleUnload);
//     window.addEventListener("unload", handleUnload);
//     document.addEventListener("visibilitychange", handleVisibilityChange);

//     return () => {
//       console.log('🧹 Nettoyage useEffect');
//       clearTimeout(entryTimeout);
//       window.removeEventListener("beforeunload", handleUnload);
//       window.removeEventListener("unload", handleUnload);
//       document.removeEventListener("visibilitychange", handleVisibilityChange);
//     };
//   }, []);

//   // ========================================
//   // FIN DU CODE DE TRACKING
//   // ========================================

//   return (
//     <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-8 px-4">


//       {/* Navigation latérale fixe - toujours visible */}
//       <nav className="hidden lg:block fixed left-8 top-1/2 -translate-y-1/2 z-40">
//         <div className="bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl border border-gray-200/50 py-4 px-2">
//           {navItems.map((item) => (
//             <button
//               key={item.id}
//               onClick={() => scrollToSection(item.id)}
//               className={`group relative w-full flex items-center gap-3 px-3 py-3 mb-2 rounded-xl transition-all duration-300 ${activeSection === item.id
//                 ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg scale-105'
//                 : 'text-gray-600 hover:bg-gray-100 hover:text-indigo-600'
//                 }`}
//               title={item.label}
//             >
//               {typeof item.icon === 'string' ? (
//                 <span className="text-xl">{item.icon}</span>
//               ) : (
//                 <item.icon className="text-lg flex-shrink-0" />
//               )}
//               <span className="text-sm font-medium whitespace-nowrap">
//                 {item.label}
//               </span>

//               {activeSection === item.id && (
//                 <span
//                   className="absolute left-0 top-1/2 -translate-y-1/2 -ml-3
//                w-0 h-0
//                border-t-6 border-b-6 border-r-8
//                border-t-transparent border-b-transparent border-r-white"
//                 />
//               )}
//             </button>
//           ))}
//         </div>
//       </nav>

   

     
      
//       <div className="max-w-5xl mx-auto">
//         {/* Header avec effet glassmorphism */}
//         <header id="header" className="relative overflow-hidden rounded-3xl mb-8 shadow-2xl">
//           <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 opacity-90"></div>
//           <div className="absolute inset-0 backdrop-blur-sm"></div>

//           <div className="relative z-10 text-white p-12">
//             <div className="flex items-center justify-center mb-6">
//               <div className="w-32 h-32 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center border-4 border-white/30 shadow-xl">
//                 <FaUser className="text-6xl text-white" />
//               </div>
//             </div>

//             <h1 className="text-5xl md:text-6xl font-black text-center mb-4 tracking-tight">
//               Ali Bouhjira
//             </h1>

//             <div className="flex justify-center mb-6">
//               <div className="inline-block px-6 py-3 bg-white/20 backdrop-blur-md rounded-full border border-white/30">
//                 <p className="text-2xl font-bold uppercase tracking-wider text-yellow-300">
//                   Ingénieur Systèmes Embarqués & IoT
//                 </p>
//               </div>
//             </div>

//             <p className="text-lg leading-relaxed text-center max-w-3xl mx-auto mb-6 text-white/95">
//               Diplômé d'un Master en IoT et fort d'un stage de fin d'études couvrant l'essentiel des systèmes embarqués, j'allie mes connaissances académiques à une capacité d'adaptation naturelle pour mener à bien des projets informatiques en équipe, quel que soit le domaine d'application.
//             </p>

//             <div className="flex flex-wrap justify-center gap-6 text-sm">
//               <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
//                 <span className="font-semibold">24 ans</span>
//               </div>
//               <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
//                 <span className="font-semibold">Permis B</span>
//               </div>
//               <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
//                 <FaMapMarkerAlt className="text-lg" />
//                 <span className="font-semibold">Douai</span>
//               </div>
//             </div>

//             <div className="flex flex-wrap justify-center gap-4 mt-6">
//               <a href="mailto:ali.bouhjira59950@gmail.com" className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-5 py-3 rounded-full hover:bg-white/20 transition-all transform hover:scale-105">
//                 <FaEnvelope className="text-lg" />
//                 <span>ali.bouhjira59950@gmail.com</span>
//               </a>
//               <a href="tel:0767482958" className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-5 py-3 rounded-full hover:bg-white/20 transition-all transform hover:scale-105">
//                 <FaPhone className="text-lg" />
//                 <span>07 67 48 29 58</span>
//               </a>
//               <a href="https://www.linkedin.com/in/ali-bouhjira-iot/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-5 py-3 rounded-full hover:bg-white/20 transition-all transform hover:scale-105">
//                 <FaLinkedin className="text-lg" />
//                 <span>Ali BOUHJIRA</span>
//               </a>
//             </div>
//           </div>
//         </header>

//         {/* Formation */}
//         <section id="formation" className="mb-8 bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-shadow duration-300">
//           <div className="flex items-center gap-3 mb-6">
//             <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
//               <FaGraduationCap className="text-2xl text-white" />
//             </div>
//             <h2 className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
//               Formation
//             </h2>
//           </div>

//           <div className="space-y-6">
//             <div className="relative pl-8 border-l-4 border-indigo-500 hover:border-purple-500 transition-colors">
//               <div className="absolute -left-2 top-0 w-4 h-4 bg-indigo-500 rounded-full"></div>
//               <h3 className="text-xl font-bold text-gray-800 mb-2">
//                 <a href="https://www.univ-lille.fr/formations/fr-00148262" target="_blank" rel="noopener noreferrer" className="hover:text-indigo-600 transition-colors">
//                   Master Systèmes Embarqués et IoT
//                 </a>
//               </h3>
//               <p className="text-indigo-600 font-semibold mb-1">Mention bien</p>
//               <p className="text-gray-600 font-medium mb-2">2025 • Université de Lille</p>
//               <p className="text-gray-600 text-sm leading-relaxed">
//                 Reverse Engineering, Analyse de risques cyber, Kernel Programming, Réseaux sans fil, Systèmes temps réel, Signal Processing.
//               </p>
//             </div>

//             <div className="relative pl-8 border-l-4 border-indigo-500 hover:border-purple-500 transition-colors">
//               <div className="absolute -left-2 top-0 w-4 h-4 bg-indigo-500 rounded-full"></div>
//               <h3 className="text-xl font-bold text-gray-800 mb-2">Licence Informatique</h3>
//               <p className="text-gray-600 font-medium mb-2">2020 • Université polytechnique de Valenciennes</p>
//               <p className="text-gray-600 text-sm leading-relaxed">
//                 Gestion de bases de données avancée, Programmation orientée objets avancée, Complexité.
//               </p>
//             </div>
//           </div>
//         </section>

//         {/* Stage */}
//         <section id="stage" className="mb-8 bg-gradient-to-br from-red-50 to-orange-50 rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-shadow duration-300 border-2 border-red-200">
//           <div className="flex items-center gap-3 mb-6">
//             <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-orange-600 rounded-xl flex items-center justify-center">
//               <FaLaptopCode className="text-2xl text-white" />
//             </div>
//             <h2 className="text-3xl font-bold text-gray-800">
//               Stage de fin d'étude chez{" "}
//               <a href="https://www.brandt.fr/" target="_blank" rel="noopener noreferrer" className="text-red-600 hover:text-red-700 underline">
//                 Brandt
//               </a>
//             </h2>
//           </div>

//           <p className="text-gray-600 font-medium mb-6">Avril à Septembre 2025 • Orléans</p>

//           <div className="bg-white rounded-2xl p-6 mb-6">
//             <h3 className="text-xl font-bold text-red-600 mb-3">Sujet</h3>
//             <p className="text-lg text-gray-800">
//               Portage d'un protocole USB vers un port série virtuel UART sur carte Renesas RX62N
//             </p>
//           </div>

//           <div className="bg-white rounded-2xl p-6 mb-6">
//             <h3 className="text-xl font-bold text-red-600 mb-4">Tâches réalisées</h3>
//             <ul className="space-y-3">
//               {[
//                 "Étude et compréhension des cahiers des charges afin de proposer des solutions adaptées.",
//                 "Développement et intégration de modules logiciels embarqués conformes aux procédures internes et aux normes qualité.",
//                 "Réalisation de plans de test, validation et traçabilité conformément aux exigences du projet.",
//                 "Collaboration avec les équipes hardware pour assurer la conformité aux spécifications et la fiabilité des produits tout en assurant sécurité et confidentialité des données."
//               ].map((task, idx) => (
//                 <li key={idx} className="flex items-start gap-3">
//                   <span className="text-red-500 text-lg mt-1">▸</span>
//                   <span className="text-gray-700">{task}</span>
//                 </li>
//               ))}
//             </ul>
//           </div>

//           <div className="bg-white rounded-2xl p-6">
//             <h3 className="text-xl font-bold text-red-600 mb-4">Environnement technique</h3>
//             <div className="space-y-4">
//               {[
//                 { title: "Microcontrôleurs & architectures", desc: "Développement en C embarqué sur microcontrôleur Renesas RX62N dans un environnement Eclipse, maîtrise des registres et de la stack." },
//                 { title: "Connectivité embarquée", desc: "Intégration Bluetooth sur ESP32, tests d'interopérabilité et conformité aux specs." },
//                 { title: "Gestion des interruptions", desc: "Conception et implémentation de handlers temps réel, priorisation et fiabilité." },
//                 { title: "Interfaces & protocoles", desc: "UART, USB et communications série pour intégration périphérique et debug." },
//                 { title: "Debug & analyse", desc: "Utilisation de débogueurs (interface eclipse de GDB), traces et analyse mémoire pour résolution des anomalies." },
//                 { title: "Gestion de version", desc: "SVN pour contrôle de version, gestion des branches et traçabilité des modifications." },
//                 { title: "Contraintes embarquées", desc: "Optimisation mémoire, gestion de la stack, et développement conforme aux spécifications." }
//               ].map((item, idx) => (
//                 <div key={idx} className="border-l-4 border-red-300 pl-4">
//                   <p className="font-semibold text-gray-800 mb-1">{item.title}</p>
//                   <p className="text-gray-600 text-sm">{item.desc}</p>
//                 </div>
//               ))}
//             </div>
//           </div>
//         </section>

//         <section id="competences" className="mb-8 bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-shadow duration-300">
//         <div className="flex items-center gap-3 mb-6">
//           <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
//             <FaReact className="text-2xl text-white" />
//           </div>
//           <h2 className="text-3xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
//             Compétences
//           </h2>
//         </div>
//         <div className="grid md:grid-cols-2 gap-6">
//           <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6">
//             <h3 className="text-lg font-bold text-green-700 mb-3">Langages de programmation</h3>
//             <div className="flex flex-wrap gap-2">
//               {langages.map(lang => (
//                 <span 
//                   key={lang.name} 
//                   className={`px-3 py-1 rounded-full text-sm font-medium shadow-sm ${
//                     lang.level === 'expert' 
//                       ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white ring-2 ring-green-300 font-bold' 
//                       : 'bg-white text-gray-700'
//                   }`}
//                 >
//                   {lang.name}
//                   {lang.level === 'expert' && <span className="ml-1"></span>}
//                 </span>
//               ))}
//             </div>
//             <p className="text-xs text-gray-500 mt-3 italic">Le langage C constitue mon cœur de compétence, complété par de solides bases en programmation orientée objet et en langages de scripting.</p>
//           </div>
//           <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6">
//             <h3 className="text-lg font-bold text-green-700 mb-3">Langues</h3>
//             <ul className="space-y-2">
//               {[
//                 { lang: "Français", level: "natif" },
//                 { lang: "Anglais", level: "B2" },
//                 { lang: "Arabe", level: "courant" },
//                 { lang: "Espagnol", level: "débutant" }
//               ].map(item => (
//                 <li key={item.lang} className="flex items-center justify-between">
//                   <span className="text-gray-700 font-medium">{item.lang}</span>
//                   <span className="px-3 py-1 bg-white rounded-full text-xs font-semibold text-green-600">{item.level}</span>
//                 </li>
//               ))}
//             </ul>
//           </div>
//           <div className="md:col-span-2 bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6">
//             <h3 className="text-lg font-bold text-green-700 mb-3">Systèmes embarqués et Connectivité</h3>
//             <ul className="grid md:grid-cols-2 gap-3">
//               {[
//                 "Développement de systèmes embarqués",
//                 "Bare metal & RTOS (Linux, FreeRTOS)",
//                 "Connectivité : Wi-Fi, Bluetooth, LoRa",
//                 "Protocoles de communication : MQTT, HTTP, FTP",
//                 "Contraintes temps réel et optimisation mémoire",
//                 "Cybersécurité : notions sur les failles de sécurité courantes",
//                 "Notions de développement Android et iOS",
//                 "Notions de Bases de données & API",
//                 "Cloud pour l'IoT : AWS IoT Core / Greengrass",
//                 "Dashboards : Grafana"
//               ].map(skill => (
//                 <li key={skill} className="flex items-start gap-2">
//                   <span className="text-green-500 text-lg mt-0.5">✓</span>
//                   <span className="text-gray-700">{skill}</span>
//                 </li>
//               ))}
//             </ul>
//           </div>
//         </div>
//       </section>

//         {/* Projets */}
//         <section id="projets" className="mb-8 bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-shadow duration-300">
//           <div className="flex items-center gap-3 mb-6">
//             <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
//               <FaGithub className="text-2xl text-white" />
//             </div>
//             <h2 className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
//               Projets Personnels
//             </h2>
//           </div>

//           <div className="space-y-4">
//             {[
//               {
//                 id: 'stateMachine', icon: FaSitemap, title: "Développement d'une librairie de machine à états", details: [
//                   "Portage de la librairie sur FreeRTOS pour satisfaire les contraintes matérielles du Raspberry Pi Pico.",
//                   "Utilisée comme outil pédagogique en thèse et au Master 1."
//                 ], tags: ["Linux", "C", "C++", "CMake", "FreeRTOS", "Git"]
//               },
//               {
//                 id: 'powerSpyCli', icon: FaBolt, title: "Développement logiciel PowerSpyCli", details: [
//                   "Participation au développement d'un outil de mesure de consommation énergétique utilisé en industrie.",
//                   "Intégration cross-platform de la communication Bluetooth.",
//                   "Création de l'IHM et réalisation de tests fonctionnels."
//                 ], tags: ["Linux", "Windows", "Python", "Bluetooth", "Git"]
//               },
//               {
//                 id: 'horseCollar', icon: FaHorse, title: "Développement de colliers connectés Arduino pour chevaux", details: [
//                   "Conception et développement d'un collier de suivi pour chevaux.",
//                   "Collecte de données (température, pouls, gyroscopie) et transmission via LoRa.",
//                   "Développement d'algorithmes en MicroPython pour Arduino Uno."
//                 ], tags: ["MQTT", "Python"]
//               },
//               {
//                 id: 'webMobileStage', icon: FaLaptopCode, title: "Stage Développement Web et Mobile (2 mois)", details: [
//                   "Développement d'applications web et mobiles en équipe.",
//                   "Participation à l'analyse des besoins, développement et tests.",
//                   "Application de la méthodologie agile (Scrum)."
//                 ], tags: ["Git", "Android Studio", "Vue.js", "Flutter"]
//               }
//             ].map((project) => (
//               <div key={project.id} className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-2xl p-6 hover:shadow-md transition-all">
//                 <div className="flex items-start gap-4">
//                   <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
//                     <project.icon className="text-xl text-white" />
//                   </div>
//                   <div className="flex-1">
//                     <h3 className="text-lg font-bold text-gray-800 mb-2">{project.title}</h3>
//                     <button
//                       onClick={() => toggleDetails(project.id)}
//                       className="flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-medium transition-colors"
//                     >
//                       {showDetails[project.id] ? "Moins d'infos" : "En savoir plus"}
//                       {showDetails[project.id] ? <FaAngleUp /> : <FaAngleDown />}
//                     </button>

//                     {showDetails[project.id] && (
//                       <div className="mt-4 space-y-3">
//                         <ul className="space-y-2">
//                           {project.details.map((detail, idx) => (
//                             <li key={idx} className="flex items-start gap-2">
//                               <span className="text-indigo-500 mt-1">•</span>
//                               <span className="text-gray-700">{detail}</span>
//                             </li>
//                           ))}
//                         </ul>
//                         <div className="flex flex-wrap gap-2 pt-2">
//                           {project.tags.map((tag, idx) => (
//                             <span key={idx} className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm font-medium">
//                               {tag}
//                             </span>
//                           ))}
//                         </div>
//                       </div>
//                     )}
//                   </div>
//                 </div>
//               </div>
//             ))}
//           </div>

//           <div className="mt-6">
//             <a href="https://github.com/alibouhjira?tab=repositories" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3 bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-all transform hover:scale-105 shadow-lg">
//               Voir plus de projets <FaGithub />
//             </a>
//           </div>
//         </section>



//         {/* soft skills*/}

//         <section id="soft-skills" className="mb-8 bg-gradient-to-br from-purple-600 via-pink-500 to-rose-500 rounded-3xl p-8 shadow-2xl overflow-hidden relative">
//           <div className="absolute inset-0 opacity-10">
//             <div className="absolute top-0 left-0 w-72 h-72 bg-white rounded-full blur-3xl"></div>
//             <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
//           </div>

//           <div className="relative z-10">
//             <div className="text-center mb-8">
//               <div className="inline-flex items-center gap-3 mb-3">
//                 <div className="h-1 w-8 bg-white/40 rounded-full"></div>
//                 <h2 className="text-3xl font-bold text-white">Soft Skills</h2>
//                 <div className="h-1 w-8 bg-white/40 rounded-full"></div>
//               </div>
//               <p className="text-white/90 text-sm">Compétences humaines qui font la différence</p>
//             </div>

//             <div className="grid md:grid-cols-5 gap-4">
//               {[
//                 {
//                   skill: "Esprit d'équipe",
//                   icon: "👥",
//                   color: "from-blue-400 to-cyan-400",
//                   description: "Collaboration efficace"
//                 },
//                 {
//                   skill: "Autonomie",
//                   icon: "🎯",
//                   color: "from-emerald-400 to-green-400",
//                   description: "Initiative personnelle"
//                 },
//                 {
//                   skill: "Curiosité",
//                   icon: "🔍",
//                   color: "from-amber-400 to-orange-400",
//                   description: "Apprentissage continu"
//                 },
//                 {
//                   skill: "Rigueur",
//                   icon: "⚡",
//                   color: "from-violet-400 to-purple-400",
//                   description: "Précision & qualité"
//                 },
//                 {
//                   skill: "Adaptation",
//                   icon: "🚀",
//                   color: "from-pink-400 to-rose-400",
//                   description: "Flexibilité & agilité"
//                 }
//               ].map((item, idx) => (
//                 <div key={idx} className="group relative">
//                   <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border-2 border-white/20 hover:border-white/40 transition-all duration-300 hover:transform hover:scale-110 hover:rotate-2 cursor-pointer h-full flex flex-col items-center text-center">
//                     {/* Icon avec animation */}
//                     <div className="text-5xl mb-3 group-hover:scale-125 transition-transform duration-300">
//                       {item.icon}
//                     </div>

//                     {/* Skill name */}
//                     <h3 className="text-white font-bold text-lg mb-2 group-hover:text-yellow-200 transition-colors">
//                       {item.skill}
//                     </h3>

//                     {/* Description - visible au hover */}
//                     <p className="text-white/70 text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-300">
//                       {item.description}
//                     </p>

//                     {/* Progress bar decorative */}
//                     <div className="mt-3 w-full h-1 bg-white/20 rounded-full overflow-hidden">
//                       <div className={`h-full bg-gradient-to-r ${item.color} rounded-full transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500`}></div>
//                     </div>
//                   </div>

//                   {/* Glow effect au hover */}
//                   <div className={`absolute inset-0 bg-gradient-to-r ${item.color} opacity-0 group-hover:opacity-20 blur-xl rounded-2xl transition-opacity duration-300 -z-10`}></div>
//                 </div>
//               ))}
//             </div>




//           </div>
//         </section>



//         {/* Références */}
//         <section id="references" className="mb-8 bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-shadow duration-300 overflow-hidden relative">
//           <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-full blur-3xl opacity-30 -mr-32 -mt-32"></div>
//           <div className="relative z-10">
//             <div className="text-center mb-8">
//               <h2 className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-2">
//                 Références Professionnelles
//               </h2>
//               <p className="text-gray-600">Personnes pouvant témoigner de mes compétences</p>
//             </div>

//             <div className="grid md:grid-cols-3 gap-6">
//               {[
//                 {
//                   name: "Giuseppe Lipari",
//                   initials: "GL",
//                   role: "Professeur à l'Université de Lille",
//                   detail: "Responsable de la filière IOT",
//                   badge: "Académique",
//                   badgeColor: "from-blue-500 to-cyan-500",
//                   linkedin: "https://www.linkedin.com/in/giuseppelipari/",
//                   quote: "Expert en systèmes temps réel"
//                 },
//                 {
//                   name: "Xavier André",
//                   initials: "XA",
//                   role: "Ingénieur R&D chez Brandt",
//                   detail: "Tuteur de stage",
//                   badge: "Industrie",
//                   badgeColor: "from-red-500 to-orange-500",
//                   email: "Xavier.Andre@groupebrandt.com",
//                   quote: "Spécialiste systèmes embarqués"
//                 },
//                 {
//                   name: "Paul ISSOGUI-AGUEWET",
//                   initials: "PI",
//                   role: "Responsable électronique chez Brandt",
//                   detail: "N+1 lors de mon stage",
//                   badge: "Management",
//                   badgeColor: "from-purple-500 to-pink-500",
//                   linkedin: "https://www.linkedin.com/in/paul-marcel-issogui-aguewet/",
//                   quote: "Leadership technique"
//                 }
//               ].map((ref, idx) => (
//                 <div key={idx} className="group relative bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 border-2 border-gray-100 hover:border-indigo-300 transition-all duration-300 hover:transform hover:scale-105 hover:shadow-xl">
//                   {/* Badge flottant */}
//                   <div className="absolute -top-3 -right-3">
//                     <div className={`px-3 py-1 bg-gradient-to-r ${ref.badgeColor} text-white text-xs font-bold rounded-full shadow-lg`}>
//                       {ref.badge}
//                     </div>
//                   </div>

//                   {/* Avatar avec initiales */}
//                   <div className="flex justify-center mb-4">
//                     <div className={`w-20 h-20 bg-gradient-to-br ${ref.badgeColor} rounded-2xl flex items-center justify-center text-white text-2xl font-bold shadow-lg transform group-hover:rotate-6 transition-transform duration-300`}>
//                       {ref.initials}
//                     </div>
//                   </div>

//                   {/* Contenu */}
//                   <div className="text-center mb-4">
//                     <h3 className="text-lg font-bold text-gray-800 mb-1">{ref.name}</h3>
//                     <p className="text-gray-600 font-semibold text-sm mb-1">{ref.role}</p>
//                     <p className="text-gray-500 text-xs mb-3">{ref.detail}</p>

//                     {/* Quote */}
//                     <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-2 mb-3">
//                       <p className="text-xs italic text-gray-600">"{ref.quote}"</p>
//                     </div>
//                   </div>

//                   {/* Boutons d'action */}
//                   <div className="space-y-2">
//                     {ref.linkedin && (
//                       <a
//                         href={ref.linkedin}
//                         target="_blank"
//                         rel="noopener noreferrer"
//                         className="flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all shadow-md hover:shadow-lg text-sm font-medium group/btn"
//                       >
//                         <FaLinkedin className="text-lg group-hover/btn:scale-110 transition-transform" />
//                         <span>Voir le profil</span>
//                       </a>
//                     )}
//                     {ref.email && (
//                       <a
//                         href={`mailto:${ref.email}`}
//                         className="flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all shadow-md hover:shadow-lg text-sm font-medium group/btn"
//                       >
//                         <FaEnvelope className="text-lg group-hover/btn:scale-110 transition-transform" />
//                         <span>Contacter</span>
//                       </a>
//                     )}
//                   </div>
//                 </div>
//               ))}
//             </div>

//             {/* Note en bas */}
//             <div className="mt-8 text-center">
//               <div className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-full border border-indigo-200">
//                 <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
//                 </svg>
//                 <p className="text-sm text-gray-700">
//                   <span className="font-semibold text-indigo-600">Lettres de recommandation</span> disponibles sur demande
//                 </p>
//               </div>
//             </div>
//           </div>
//         </section>

//         {/* Centres d'intérêt */}
//         <section id="interets" className="mb-8 bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-shadow duration-300">
//           <h2 className="text-2xl font-bold text-indigo-700 mb-6">Centres d'intérêt</h2>
//           <div className="space-y-4">
//             <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6">
//               <h3 className="text-lg font-bold text-gray-800 mb-2">Sport</h3>
//               <p className="text-gray-700">Pratique régulière de la musculation et du football, améliorant ma rigueur, ma cohésion d'équipe, ma persévérance et ma capacité à relever des défis.</p>
//             </div>
//             <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-6">
//               <h3 className="text-lg font-bold text-gray-800 mb-2">Jeux vidéo compétitifs</h3>
//               <p className="text-gray-700">Participation active à des compétitions en équipe, renforçant ma communication, ma stratégie et ma capacité à travailler sous pression.</p>
//             </div>
//           </div>
//         </section>



//         {/* Documents annexes */}
//         <section id="memoire" className="mb-8 bg-gradient-to-br from-slate-50 via-white to-blue-50 rounded-3xl p-10 shadow-2xl overflow-hidden relative border border-slate-200">
//           {/* Decorative elements */}
//           <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full blur-3xl opacity-20 -mr-48 -mt-48"></div>
//           <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-purple-100 to-pink-100 rounded-full blur-3xl opacity-20 -ml-32 -mb-32"></div>

//           <div className="relative z-10">
//             {/* Header */}
//             <div className="text-center mb-8">
//               <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl mb-4 shadow-lg">
//                 <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
//                 </svg>
//               </div>
//               <h2 className="text-3xl font-bold text-gray-800 mb-2">
//                 Mémoire de Recherche
//               </h2>
//               <p className="text-gray-600 text-sm">Master Systèmes Embarqués et IoT • Université de Lille • 2025</p>
//             </div>

//             {/* Main content card */}
//             <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
//               {/* Title bar */}
//               <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 px-8 py-6">
//                 <h3 className="text-2xl font-bold text-white leading-tight mb-2">
//                   Révolutionner le traitement de la schizophrénie avec l'Internet des Objets Médicaux
//                 </h3>
//                 <div className="flex items-center gap-4 text-white/90 text-sm">
//                   <span className="flex items-center gap-2">
//                     <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
//                     </svg>
//                     2025
//                   </span>
//                   <span className="flex items-center gap-2">
//                     <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
//                     </svg>
//                     Ali Bouhjira
//                   </span>
//                 </div>
//               </div>

//               {/* Abstract section */}
//               <div className="p-8">
//                 <div className="mb-6">
//                   <div className="flex items-center gap-2 mb-4">
//                     <div className="w-1 h-6 bg-gradient-to-b from-blue-600 to-indigo-600 rounded-full"></div>
//                     <h4 className="text-lg font-bold text-gray-800">Résumé</h4>
//                   </div>
//                   <p className="text-gray-700 leading-relaxed text-justify text-sm">
//                     Ce mémoire examine l'utilisation de l'<span className="font-semibold text-indigo-600">Internet des Objets Médicaux (IoMT)</span> pour améliorer la prise en charge de la schizophrénie. Il explore la possibilité d'intégrer l'IoMT pour surmonter les limites des traitements actuels, en particulier concernant les symptômes négatifs et cognitifs, l'adhérence thérapeutique et la détection précoce des rechutes. L'étude se concentre sur des technologies telles que l'<span className="font-semibold text-indigo-600">électroencéphalogramme connecté</span>, les <span className="font-semibold text-indigo-600">dispositifs portables</span> pour surveiller les signes vitaux et comportementaux, et les smartphones pour un suivi passif des données. Les défis éthiques, techniques et d'interopérabilité sont également abordés. En conclusion, l'IoMT présente un potentiel pour un suivi plus précis, des <span className="font-semibold text-indigo-600">interventions basées sur l'IA</span> et l'amélioration de la qualité de vie des patients.
//                   </p>
//                 </div>

//                 {/* Key themes */}
//                 <div className="mb-6">
//                   <div className="flex items-center gap-2 mb-4">
//                     <div className="w-1 h-6 bg-gradient-to-b from-indigo-600 to-purple-600 rounded-full"></div>
//                     <h4 className="text-lg font-bold text-gray-800">Thématiques clés</h4>
//                   </div>
//                   <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
//                     {[
//                       { icon: "🏥", label: "IoMT & Santé" },
//                       { icon: "🧠", label: "Schizophrénie" },
//                       { icon: "📊", label: "EEG connecté" },
//                       { icon: "⌚", label: "Dispositifs portables" },
//                       { icon: "🤖", label: "Intelligence Artificielle" },
//                       { icon: "🔒", label: "Éthique & Sécurité" }
//                     ].map((theme, idx) => (
//                       <div key={idx} className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100 hover:border-indigo-300 transition-colors">
//                         <span className="text-xl">{theme.icon}</span>
//                         <span className="text-xs font-medium text-gray-700">{theme.label}</span>
//                       </div>
//                     ))}
//                   </div>
//                 </div>

//                 {/* Stats */}
//                 <div className="grid grid-cols-3 gap-4 mb-6 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl">
//                   <div className="text-center">
//                     <div className="text-3xl font-bold text-indigo-600 mb-1">IoT</div>
//                     <div className="text-xs text-gray-600">Domaine d'étude</div>
//                   </div>
//                   <div className="text-center border-l border-r border-indigo-200">
//                     <div className="text-3xl font-bold text-indigo-600 mb-1">M2</div>
//                     <div className="text-xs text-gray-600">Niveau Master</div>
//                   </div>
//                   <div className="text-center">
//                     <div className="text-3xl font-bold text-indigo-600 mb-1">2025</div>
//                     <div className="text-xs text-gray-600">Année</div>
//                   </div>
//                 </div>

//                 {/* Action button */}
//                 <button
//                   onClick={() => openModal("MemoireMaster_AliBouhjira.pdf")}
//                   className="group w-full flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:via-indigo-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]"
//                 >
//                   <FaFilePdf className="text-2xl group-hover:scale-110 transition-transform" />
//                   <div className="text-left">
//                     <div className="font-bold text-base">Consulter le mémoire complet</div>
//                     <div className="text-xs text-white/80">Cliquez pour ouvrir le document PDF</div>
//                   </div>
//                   <svg className="w-5 h-5 ml-auto group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
//                   </svg>
//                 </button>

//                 {/* Additional info */}
//                 <div className="mt-6 flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
//                   <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
//                   </svg>
//                   <p className="text-xs text-gray-700 leading-relaxed">
//                     Ce mémoire de recherche a été réalisé dans le cadre du Master 2 Systèmes Embarqués et IoT à l'Université de Lille, sous la direction du Professeur Mohamed Daoudi. Il représente une contribution à l'intersection de l'IoT et de la santé mentale.
//                   </p>
//                 </div>
//               </div>
//             </div>
//           </div>
//         </section>


//         {/* Partage */}
//         <section id="partage" className="bg-gradient-to-br from-indigo-600 to-purple-600 rounded-3xl p-8 shadow-2xl">
//           <h3 className="text-2xl font-bold text-white text-center mb-6">Partager ce CV</h3>
//           <div className="flex flex-wrap justify-center gap-4">
//             {[
//               { icon: FaLinkedin, label: "LinkedIn", action: shareOnLinkedIn, color: "bg-[#0077B5] hover:bg-[#006097]" },
//               { icon: FaWhatsapp, label: "WhatsApp", action: shareOnWhatsApp, color: "bg-[#25D366] hover:bg-[#1DA851]" },
//               { icon: FaEnvelope, label: "Email", action: shareByEmail, color: "bg-[#690fff] text-gray-800 hover:bg-[#090fff]" },
//               { icon: BsMicrosoftTeams, label: "Teams", action: shareOnTeams, color: "bg-[#464EB8] hover:bg-[#3b4099]" }
//             ].map((share, idx) => (
//               <button key={idx} onClick={share.action} className={`flex items-center gap-2 px-6 py-3 ${share.color} text-white rounded-xl transition-all transform hover:scale-105 shadow-lg`}>
//                 <share.icon className="text-xl" />
//                 <span className="font-medium">{share.label}</span>
//               </button>
//             ))}
//           </div>
//           <p className="text-center text-white/90 mt-6 text-sm">Merci pour votre lecture !</p>
//         </section>

//         {/* Modal */}
//         {isOpen && (
//           <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex justify-center items-center z-50 p-4" onClick={closeModal}>
//             <div className="bg-white w-full max-w-6xl h-5/6 rounded-2xl shadow-2xl flex flex-col" onClick={e => e.stopPropagation()}>
//               <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
//                 <h3 className="text-lg font-semibold text-gray-800">Document PDF</h3>
//                 <button onClick={closeModal} className="text-gray-600 hover:text-gray-900 text-3xl font-bold w-10 h-10 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors">
//                   ×
//                 </button>
//               </div>
//               <div className="flex-1 overflow-auto">
//                 <iframe src={pdfUrl} className="w-full h-full" title="PDF Viewer" />
//               </div>
//             </div>
//           </div>
//         )}
//       </div>
//     </div>
//   );
// };

// export default CV;